import React from 'react';
import { Edit, Trash2, MapPin, AlertTriangle } from 'lucide-react';
import EmptyState from '../Common/EmptyState';
import SlaBadge from '../Common/SlaBadge';
import Can from '../Common/Can';
import { P } from '../../constants/permissions';

const STATUS_LABELS = {
  1: 'Nova',
  2: 'Em análise',
  3: 'Atendida',
  4: 'Encaminhada',
  5: 'Improcedente',
  6: 'Não Localizada',
  7: 'Em Monitoramento',
  8: 'Auto de Infração',
  9: 'Arquivada',
  10: 'Enviada p/ Arquivamento',
  11: 'Enviada p/ Jurídico',
  12: 'Enviada p/ Gabinete',
  13: 'Enviada p/ Licenciamento',
  14: 'Notificação',
  15: 'Interditada',
  16: 'Embargada',
};

const ComplaintTable = ({
  complaints,
  categories = [],
  enterprises = [],
  licenses = [],
  isLoading,
  onEdit,
  onDelete,
  onCreate,
}) => {
  const getCategoryName = (categoryId) => {
    const cat = categories.find((c) => c.id === categoryId);
    return cat ? cat.name : categoryId || '—';
  };
  const getEnterpriseName = (id) => {
    if (!id) return '—';
    const row = enterprises.find((e) => String(e.id) === String(id));
    return row ? row.name || row.nome || id : id;
  };
  const getLicenseName = (id) => {
    if (!id) return '—';
    const row = licenses.find((l) => String(l.id) === String(id));
    return row ? row.numero || id : id;
  };

  const getStatusLabel = (status) => STATUS_LABELS[status] || `Status ${status}`;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!complaints?.length) {
    return (
      <EmptyState
        icon={AlertTriangle}
        title="Nenhuma denúncia encontrada"
        description="Não há registros com os filtros atuais ou ainda não há denúncias cadastradas."
        action={
          onCreate ? (
            <Can permission={P.COMPLAINT_ACCESS}>
              <button type="button" className="btn btn-primary" onClick={onCreate}>
                Nova denúncia
              </button>
            </Can>
          ) : null
        }
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Categoria</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Local</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empreendimento</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Licença</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prazo</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {complaints.map((complaint) => (
            <tr key={complaint.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {complaint.data ? new Date(complaint.data).toLocaleDateString('pt-BR') : '—'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                {getCategoryName(complaint.category_id)}
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center gap-1 text-sm text-gray-600 max-w-xs truncate">
                  <MapPin className="w-4 h-4 flex-shrink-0 text-gray-400" />
                  {complaint.cidade && complaint.bairro
                    ? `${complaint.bairro}, ${complaint.cidade}`
                    : complaint.local || '—'}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                {getEnterpriseName(complaint.enterprise_id)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                {getLicenseName(complaint.license_id)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                  {getStatusLabel(complaint.status)}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                {complaint.prazo_atendimento ? (
                  <SlaBadge prazo={complaint.prazo_atendimento} />
                ) : (
                  '—'
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <Can permission={P.COMPLAINT_ACCESS}>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => onEdit(complaint)}
                      className="text-primary-600 hover:text-primary-900 p-1 rounded hover:bg-primary-50"
                      title="Editar"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(complaint.id)}
                      className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                      title="Excluir"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </Can>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ComplaintTable;
